import { type ReactNode } from 'react';
import Confetti from 'react-confetti';
import { Check, CheckCircle2, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
  Label,
  Steps,
  type StepItem,
} from '@design-system';
import { type ConsentItem } from '@shared/consents';
import { ConsentPdfViewer } from '@/components/ConsentPdfViewer';
import { useConsentSelection } from '@/hooks/useConsentSelection';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

interface ClientPreApplicationConsentFlowProps {
  clientName: string;
  isCareScout: boolean;
  isConsentPending: boolean;
  isSubmitting: boolean;
  isPacketDownloaded: boolean;
  celebrateCompletion: boolean;
  downloadAction: ReactNode;
  downloadAgainAction: ReactNode;
  onSubmit: (consentIds: string[]) => void;
}

function getConsentSteps(currentStep: 1 | 2): StepItem[] {
  return [
    {
      id: 'provide-consents',
      label: 'Step 1: Provide Consents',
      status: currentStep === 1 ? 'current' : 'completed',
    },
    {
      id: 'download-packet',
      label: 'Step 2: Download Take Home Packet',
      status: currentStep === 2 ? 'current' : 'upcoming',
    },
  ];
}

function ConsentDescription({ item }: { item: ConsentItem }) {
  return (
    <ReactMarkdown
      components={{
        p: ({ node, ...props }) => (
          <p
            {...props}
            className="text-foreground text-sm leading-relaxed"
          />
        ),
        ul: ({ node, ...props }) => (
          <ul
            {...props}
            className="text-foreground list-disc space-y-1 pl-5 text-sm leading-relaxed"
          />
        ),
      }}
    >
      {item.formattedDescription?.trim() || item.description}
    </ReactMarkdown>
  );
}

export function ClientPreApplicationConsentFlow({
  clientName,
  isCareScout,
  isConsentPending,
  isSubmitting,
  isPacketDownloaded,
  celebrateCompletion,
  downloadAction,
  downloadAgainAction,
  onSubmit,
}: ClientPreApplicationConsentFlowProps) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const { consentItems, consentStates, setConsentChecked, allConsentsChecked } =
    useConsentSelection({
      userType: 'client',
      consentType: 'start',
      isCareScout,
      requireConsentItems: true,
    });

  if (isPacketDownloaded) {
    return (
      <div className="relative overflow-hidden px-5 py-10 sm:px-8 sm:py-14">
        {celebrateCompletion && !prefersReducedMotion && (
          <Confetti
            recycle={false}
            numberOfPieces={70}
            gravity={0.12}
            initialVelocityY={8}
            className="fixed! pointer-events-none inset-0 z-50"
          />
        )}
        <div className="mx-auto flex max-w-xl flex-col items-center text-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-green-100 text-green-800">
            <Check className="size-6" />
          </div>
          <h1 className="text-foreground mt-5 text-3xl font-semibold tracking-tight">
            You are all set
          </h1>
          <p className="text-foreground mt-4 text-sm leading-relaxed">
            Your advisor will continue the application from here.
            <br />
            Coordinate with them on your next steps.
          </p>

          <ItemGroup className="mt-7 w-full rounded-xl border p-3 text-left">
            <Item size="sm">
              <ItemMedia>
                <CheckCircle2 className="size-5 text-green-700" />
              </ItemMedia>
              <ItemContent>
                <ItemTitle className="text-foreground font-normal">
                  Your consent is on file
                </ItemTitle>
              </ItemContent>
            </Item>
            <Item size="sm">
              <ItemMedia>
                <CheckCircle2 className="size-5 text-green-700" />
              </ItemMedia>
              <ItemContent>
                <ItemTitle className="text-foreground font-normal">
                  Your take home packet has been downloaded
                </ItemTitle>
              </ItemContent>
            </Item>
          </ItemGroup>

          {downloadAgainAction && (
            <div className="text-muted-foreground mt-6 flex flex-wrap items-center justify-center gap-1 text-sm">
              <span>Didn’t get the file?</span>
              {downloadAgainAction}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!isConsentPending) {
    return (
      <div className="space-y-6 px-5 py-8 sm:px-8 sm:py-10">
        <Steps
          aria-label="Consent progress"
          items={getConsentSteps(2)}
        />
        <div>
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">
            Waterlily Consent Agreements
          </h1>
          <p className="text-foreground mt-2 leading-relaxed">
            Your consents have been recorded. Download your take home packet to continue.
          </p>
        </div>

        <Card className="gap-4 py-5 shadow-none">
          <CardHeader className="gap-4 px-5">
            <div className="flex items-center gap-3">
              <span className="flex size-6 items-center justify-center rounded-full bg-green-100 text-green-800">
                <Check className="size-4" />
              </span>
              <CardTitle className="text-foreground">Consents Recorded</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="border-t px-5 pt-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-foreground text-sm font-semibold">
                  Voluntary Consent to Receive Documents Electronically
                </p>
                <p className="text-muted-foreground mt-1 text-xs">{clientName}</p>
              </div>
              <Badge variant="success">Consented</Badge>
            </div>
          </CardContent>
        </Card>

        <Item
          variant="outline"
          className="bg-card rounded-xl"
        >
          <ItemMedia variant="icon">
            <FileText />
          </ItemMedia>
          <ItemContent>
            <ItemTitle>Take Home Packet</ItemTitle>
            <ItemDescription>PDF · quote, coverage, recorded consent</ItemDescription>
          </ItemContent>
        </Item>

        <div className="[&>button]:h-14 [&>button]:w-full [&>button]:text-base">
          {downloadAction}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-5 py-8 sm:px-8 sm:py-10">
      <Steps
        aria-label="Consent progress"
        items={getConsentSteps(1)}
      />
      <div>
        <h1 className="text-foreground text-2xl font-semibold tracking-tight">
          Waterlily Consent Agreements
        </h1>
        <p className="text-foreground mt-2 leading-relaxed">
          Please review and agree to the following consent statements to proceed with your
          application.
        </p>
      </div>

      <div className="space-y-4">
        {consentItems.map(item => (
          <Card
            key={item.id}
            className="gap-5 p-5 shadow-none sm:p-6"
          >
            <CardTitle className="text-foreground text-base">{item.title}</CardTitle>
            <ConsentDescription item={item} />
            {isCareScout && (
              <ConsentPdfViewer
                className="bg-muted min-h-72 rounded-md border"
                fallbackClassName="p-4 text-sm"
              />
            )}
          </Card>
        ))}
      </div>

      {consentItems.map(item => {
        const checked = consentStates[item.id] || false;
        return (
          <Item
            key={item.id}
            asChild
            variant="outline"
            className={
              checked
                ? 'border-primary bg-primary/5 min-h-16 rounded-xl border-2 p-5'
                : 'border-primary/40 hover:border-primary bg-background min-h-16 rounded-xl border-2 p-5'
            }
          >
            <Label htmlFor={`client-${item.id}`}>
              <Checkbox
                id={`client-${item.id}`}
                data-testid="client-application-consent-checkbox"
                checked={checked}
                onCheckedChange={value => setConsentChecked(item.id, value === true)}
                className="size-7 rounded-md [&_svg]:size-5"
              />
              <ItemContent>
                <ItemTitle className="text-foreground text-base font-semibold">
                  {item.checkbox}
                </ItemTitle>
              </ItemContent>
            </Label>
          </Item>
        );
      })}

      <Button
        data-testid="client-application-consent-submit-button"
        size="lg"
        className="h-14 w-full text-base"
        disabled={!allConsentsChecked || isSubmitting}
        onClick={() => onSubmit(consentItems.map(item => item.id))}
      >
        Record Primary Client Consents
      </Button>
    </div>
  );
}
